import { action, makeObservable, observable } from 'mobx';
import { transformExtent } from 'ol/proj';
import { observer } from 'mobx-react';
import * as React from 'react';
import { closestCenter, DndContext, DragEndEvent, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Local imports
import './TileLayersPanelComponent.scss';
import { lumiDbStore } from '@shared/reality-capture/lumidb-store';
import { Tree as TreeNative, TreeNode } from '@shared/ui-native/Tree';
import { Tree as TreeComponent, type TreeNodeData, collectAllFolderIds } from 'design-system';
import { TreeToolbar } from '@shared/ui-react/TreeToolbar';
import { MapWrapper } from '@shared/openlayers/MapWrapper';
import { MapTileLayers } from '@openlayers/tile-layers/MapTileLayers';
import { IMapTilelayer } from '@shared/openlayers/tile-layers/TileLayerService';
import { FileTreeService } from '@shared/services/FileTreeService';
import { Catch } from '@shared/ui-react/Catch';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { TabPane, Tabs } from '@shared/ui-react/Tabs';
import { decodeHtmlEntities } from '@shared/utils/utils';
import { alphaNumSort } from '@shared/utils/utils-sort';

@observer
export class TileLayersPanelComponent extends React.Component<{
    treeData: ITreeData | null;
    tileLayers: MapTileLayers;
    mapWrapper: MapWrapper;
}> {
    @observable.ref treeWithFakeLayers: ITreeData = null;
    @observable.ref expandedIds: (number | string)[] = [];
    @observable.ref selectedId: number | string | null = null;
    @observable filterText = '';
    @observable expandOnStart = 'all';
    @observable nodesRevision = 0;
    @observable isLoading = false;

    /** Headless native tree — data management only (filtering, storage, uuid lookup). Rendering is done by the design-system Tree. */
    tree: TreeNative = null;

    // Plain (non-prop) reference for the public API below. mobx-react forbids
    // reading this.props of an observer component from another component's
    // reactive context (e.g. GlobalSearch render → getItemActions → isLayerActive).
    private tileLayers: MapTileLayers;

    mapLayerUuidToFileUuid: Map<string, string> = new Map();
    fileUuidToMapLayerUuid: Map<string, string> = new Map();

    constructor(props: { treeData: ITreeData | null; tileLayers: MapTileLayers; mapWrapper: MapWrapper }) {
        super(props);
        this.tileLayers = props.tileLayers;
        makeObservable(this);
    }

    componentDidMount() {
        if (this.props.tileLayers) {
            this.init();
        }
    }

    componentDidUpdate(prevProps: any) {
        this.tileLayers = this.props.tileLayers;
        if (prevProps.treeData !== this.props.treeData) {
            this.init();
        }
    }

    @action setExpandedIds = (ids: (number | string)[]) => {
        this.expandedIds = ids;
    };

    @action setSelectedId = (id: number | string | null) => {
        this.selectedId = id;
    };

    @action setFilterText = (text: string) => {
        this.filterText = text;
    };

    @action setExpandOnStart = (value: string) => {
        this.expandOnStart = value;
    };

    @action bumpNodesRevision = () => {
        this.nodesRevision++;
    };

    getActivePointCloudFileUuids(): string[] {
        return this.props.tileLayers.activeUuids.map(uuid => this.mapLayerUuidToFileUuid.get(uuid)).filter(Boolean) as string[];
    }

    getActiveDemFileUuids(): string[] {
        const fileUuids: string[] = this.props.tileLayers.activeUuids
            .map(uuid => {
                const layer = this.props.tileLayers.layersDefinitions.find(l => l.uuid === uuid);
                return layer?.demUrl ? this.mapLayerUuidToFileUuid.get(uuid) : null;
            })
            .filter(Boolean) as string[];
        return fileUuids;
    }

    async init() {
        if (this.treeWithFakeLayers) {
            return; // already initialized
        }

        let treeWithFakeLayers: ITreeData = {
            id: kuura.getRootFolderId(),
            models: [],
            folders: [],
        } as any;

        // only wait for file tree if any layer is actually linked to file tree node
        if (this.props.tileLayers.layersDefinitions.some(ld => ld.fileUuid)) {
            if (this.props.treeData) {
                treeWithFakeLayers = JSON.parse(JSON.stringify(this.props.treeData));
                if (!treeWithFakeLayers.models) treeWithFakeLayers.models = [];
            } else {
                // tree not loaded, try again later
                return null;
            }
        }

        this.props.tileLayers.layersDefinitions.forEach(ld => {
            let target = treeWithFakeLayers;

            // If layer linked to file, add it to folder of that file
            if (ld.fileUuid) {
                const node = FileTreeService.findNodeByUuid(treeWithFakeLayers, ld.fileUuid);
                if (node && node.parentUuid) {
                    target = FileTreeService.findNodeByUuid(treeWithFakeLayers, node.parentUuid);
                }
            }

            this.mapLayerUuidToFileUuid.set(ld.uuid, ld.fileUuid);
            this.fileUuidToMapLayerUuid.set(ld.fileUuid, ld.uuid);

            const model: Partial<ITreeData> & { projectLayer: boolean } = {
                name: ld.name,
                id: ld.id,
                uuid: ld.uuid,
                projectLayer: true,
                models: [],
                description: null,
                filesize: 1,
                timestamp: 0,
                version: 1,
                bbox: ld.bbox,
            };

            target.models.push(model as ITreeData);
        });

        FileTreeService.eachPostOrder(treeWithFakeLayers, 0, node => {
            if (node.id !== kuura.getRootFolderId() && node.models) {
                node.models = node.models.sort((a, b) => alphaNumSort(a.name, b.name));
            }
        });

        this.tree = new TreeNative({
            renderView: false,
            showRoot: false,
            showCheckboxes: true,
            selectionCheckbox: false,
            checkOnHighlightSelected: true,
            propagateCheckbox: 'deep',
            hideEmptyFolders: true,
            hideEmptyFoldersAlways: true,
            showContextMenu: false,
            storageKey: 'map-tile-layers-panel',
            expandOnStart: 'all',
            sortChildren: false,
            // projectLayer marks the fake tile-layer nodes injected in init(); it is not part of ITreeData
            childFilter: c => Boolean(c.tolerance || (c as any).projectLayer),
        });

        this.tree.initAndRender(treeWithFakeLayers, true);

        // Sync persisted expand-on-start setting from native tree storage
        const storedState = this.tree.getStoredState();
        if (storedState?.expandOnStart) {
            this.setExpandOnStart(storedState.expandOnStart);
        }

        // Mirror native tree's actual expanded state into the DS tree.
        // The DS Tree only respects expandedIds, not expandOnStart.
        const expandedDsIds: (number | string)[] = [];
        this.tree.each(c => {
            if (c.isFolder() && c.state.expanded) {
                expandedDsIds.push(`folder-${c.id}`);
            }
        }, true);
        this.setExpandedIds(expandedDsIds);

        this.treeWithFakeLayers = treeWithFakeLayers;

        await this.applySecondaryIcons();
    }

    /** Bbox + cross-section availability icons, shown by the DS tree via convertNativeNode. */
    private applySecondaryIcons = async (): Promise<void> => {
        if (!this.tree) return;

        // Bounding box available → fit-to-screen icon (was showBoundingBox on the old tree wrapper)
        this.tree.each(
            child => {
                if (!child.secondaryIcon && Array.isArray(child?.ajaxData?.bbox) && child?.ajaxData?.bbox.length) {
                    child.setSecondaryIcon('iki iki-fit-to-screen', 'Bounding box available');
                }
            },
            true,
            true
        );

        // Point cloud files available for cross section
        if (lumiDbStore.available) {
            const pointCloudUuids = await lumiDbStore.getPointCloudFileUuids();
            pointCloudUuids.forEach(uuid => {
                const node = this.tree.getByUuid(this.fileUuidToMapLayerUuid.get(uuid));
                if (node) {
                    node.setSecondaryIcon('iki iki-info', __('js_map_cross_section.layer_available'));
                }
            });
        }

        this.bumpNodesRevision();
    };

    private convertNativeNode = (node: TreeNode): TreeNodeData => {
        const isFolder = node.isFolder();
        return {
            // Files use the layer uuid as the DS tree id so checkedIds can be
            // tileLayers.activeUuids directly. Folder ids get a prefix to avoid
            // colliding with file ids (backend shares the numeric sequence).
            id: isFolder ? `folder-${node.id}` : node.uuid,
            uuid: node.uuid,
            name: decodeHtmlEntities(node.ajaxData?.name ?? node.text ?? ''),
            type: isFolder ? 'folder' : 'file',
            secondaryIcon: node.secondaryIcon
                ? <i className={node.secondaryIcon} title={node.secondaryIconTooltip ?? undefined} aria-label={node.secondaryIconTooltip ?? undefined} />
                : undefined,
            children: node.children?.filter(child => !child.state.hidden && !child.state.filtered).map(this.convertNativeNode),
            data: node,
        };
    };

    // Drops folders whose entire subtree has no file leaves (hideEmptyFoldersAlways).
    private removeEmptyFolders = (nodes: TreeNodeData[]): TreeNodeData[] =>
        nodes.reduce<TreeNodeData[]>((acc, node) => {
            if (node.type !== 'folder') {
                acc.push(node);
            } else {
                const filteredChildren = this.removeEmptyFolders(node.children ?? []);
                if (filteredChildren.length > 0) acc.push({ ...node, children: filteredChildren });
            }
            return acc;
        }, []);

    private getViewNodes(): TreeNodeData[] {
        if (!this.tree) return [];
        void this.nodesRevision; // mobx tracking: native tree node state is not observable

        const raw = this.tree.children.filter(node => !node.state.hidden && !node.state.filtered).map(this.convertNativeNode);
        return this.removeEmptyFolders(raw);
    }

    /* ─── Tree callbacks ─── */

    private handleSelect = (node: TreeNodeData) => {
        this.setSelectedId(node.id);

        const bbox = (node.data as TreeNode)?.ajaxData?.bbox;
        if (!bbox) return;

        const extent = transformExtent(bbox, 'EPSG:3857', this.props.mapWrapper.getProjection());
        this.props.mapWrapper.fitExtent(extent);
    };

    private handleCheck = (node: TreeNodeData) => {
        if (node.type !== 'file') return;
        this.props.tileLayers.activateByIdOrUuid(node.uuid, false);
    };

    private handleUncheck = (node: TreeNodeData) => {
        if (node.type !== 'file') return;
        this.props.tileLayers.deactivateLayer(node.uuid);
    };

    /* ─── Public API (used by external code, e.g. map search) ─── */

    public isLayerActive(uuid: string): boolean {
        return this.tileLayers.activeUuids.includes(uuid);
    }

    public setLayerActive(uuid: string, active: boolean): void {
        if (active) {
            this.tileLayers.activateByIdOrUuid(uuid, false);
            return;
        }
        this.tileLayers.deactivateLayer(uuid);
    }

    /** Deactivates all other layers and activates only the given one. */
    public activateSolo(uuid: string): void {
        this.tileLayers.activateByIdOrUuid(uuid, true);
    }

    render() {
        const checkedUuids = this.props.tileLayers.activeUuids;
        if (!this.treeWithFakeLayers || !this.tree) return <div className='overlay-loading no-bg' style={{ height: '100%' }}></div>;

        return (
            <div className='tiles-panel__wrapper'>
                <Catch>
                    <nav className='flex-row sidebar-toolbar ' style={{ justifyContent: 'end' }}>
                        <Dropdown closeOnBodyClick={true} buttonStyle={{ paddingLeft: 10, paddingRight: 10 }} className='ik-button-secondary--subtle'>
                            <i className='iki iki-more' />
                            <ul className='dropdown-list clickable'>
                                <li onClick={this.props.tileLayers.deactivateAll}>{__('general.deselect_all')}</li>
                            </ul>
                        </Dropdown>
                    </nav>

                    <Tabs displayValue='flex' flexDirection='column'>
                        <TabPane title={__('js_map.layers.all')}>
                            {/* Fixed slot: toolbar keeps its natural height, never scrolls */}
                            <div style={{ flexShrink: 0 }}>
                                <TreeToolbar
                                    filterText={this.filterText}
                                    onFilterChange={this.setFilterText}
                                    onExpandAll={() => this.setExpandedIds(Array.from(collectAllFolderIds(this.getViewNodes())))}
                                    onCollapseAll={() => this.setExpandedIds([])}
                                    hideEmptyFolders={true}
                                    hideEmptyFoldersAlways={true}
                                    expandOnStart={this.expandOnStart}
                                    onSetExpandOnStart={value => {
                                        this.setExpandOnStart(value);
                                        this.tree?.setExpandOnStart(value as any);
                                    }}
                                />
                            </div>
                            {/* Flexible slot: tree fills remaining height; its internal
                                overflow-auto container provides the single scrollbar.
                                min-h-0 overrides the DS Tree's default min-h-[200px]. */}
                            <TreeComponent
                                className='min-h-0 flex-1'
                                nodes={this.getViewNodes()}
                                showRoot={false}
                                checkable={true}
                                checkedIds={[...checkedUuids]}
                                selectedId={this.selectedId}
                                expandedIds={this.expandedIds}
                                onExpandedIdsChange={this.setExpandedIds}
                                hideEmptyFolders={true}
                                sortChildren={false}
                                filterText={this.filterText}
                                onNodeSelected={this.handleSelect}
                                onNodeChecked={this.handleCheck}
                                onNodeUnchecked={this.handleUncheck}
                            />
                        </TabPane>
                        <TabPane title={__('js_map.layers.activated')}>
                            <div style={{ overflow: 'auto' }}>
                                {checkedUuids.length ? (
                                    <LayersList mapWrapper={this.props.mapWrapper} mapTileLayers={this.props.tileLayers} onSortEnd={this.onSortEnd} />
                                ) : (
                                    <div className='text-center'>{__('js_map.no_tile_layers_selected')}</div>
                                )}
                            </div>
                        </TabPane>
                    </Tabs>
                </Catch>
            </div>
        );
    }

    onSortEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const activeUuids = [...this.props.tileLayers.activeUuids];
            const oldIndex = activeUuids.findIndex(uuid => uuid === active.id);
            const newIndex = activeUuids.findIndex(uuid => uuid === over.id);

            const newActiveUuids = arrayMove(activeUuids, oldIndex, newIndex);
            this.props.tileLayers.activeUuids = newActiveUuids;
            this.props.tileLayers.resortLayersZindex();
        }
    };
}

interface ILayersListProps {
    mapWrapper: MapWrapper;
    mapTileLayers: MapTileLayers;
    onSortEnd: (event: DragEndEvent) => void;
}

const LayersList = observer(({ mapWrapper, mapTileLayers, onSortEnd }: ILayersListProps) => {
    const sensors = useSensors(useSensor(MouseSensor), useSensor(TouchSensor));

    const tileLayers: MapTileLayers = mapTileLayers;
    const onlyActive = tileLayers.activeUuids.map(uuid => tileLayers.layersDefinitions.find(ld => ld.uuid === uuid));

    return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onSortEnd} modifiers={[restrictToVerticalAxis, restrictToParentElement]}>
            <SortableContext items={tileLayers.activeUuids} strategy={verticalListSortingStrategy}>
                <div>
                    {onlyActive.map(layer => (
                        <SortableLayerItem key={layer.uuid} id={layer.uuid} layer={layer} mapTileLayers={tileLayers} mapWrapper={mapWrapper} />
                    ))}
                </div>
            </SortableContext>
        </DndContext>
    );
});

interface ILayerItemProps {
    mapWrapper: MapWrapper;
    layer: IMapTilelayer;
    mapTileLayers: MapTileLayers;
    style?: React.CSSProperties;
    dragHandleProps?: any;
}

const LayerItem = observer(({ mapWrapper, layer, mapTileLayers, style = {}, dragHandleProps = {} }: ILayerItemProps) => {
    const isChecked = mapTileLayers.activeUuids.includes(layer.uuid);

    function zoomToLayer() {
        const extent = transformExtent(layer.bbox, 'EPSG:3857', mapWrapper.getProjection());
        mapWrapper.fitExtent(extent);
    }

    return (
        <div className='tiles-list__item' style={style}>
            <span className='sortable__drag-handle' {...dragHandleProps}>
                ፧
            </span>
            <div className='flex-row'>
                <div className='flex-row ai-center tiles-list__item__name'>
                    <span>
                        <input type='checkbox' checked={isChecked} onClick={() => mapTileLayers.toggleLayer(layer.uuid)} readOnly />
                    </span>
                    <span className='name'>
                        {layer.name}
                        {mapTileLayers.connectionProblem[layer.uuid] && (
                            <span style={{ marginLeft: 5 }} className='badge badge-warning' title={__('js_map.layers.connection_problems')}>
                                !
                            </span>
                        )}
                    </span>
                    {layer.bbox && (
                        <div className='flex-row ai-center tiles-list__item__zoom' onClick={() => zoomToLayer()}>
                            <i className='pi pi-expand'></i>
                        </div>
                    )}
                </div>
                {isChecked && (
                    <div className='flex-row ai-center gap-third tiles-list__item__slider'>
                        <input
                            type='range'
                            min={20}
                            max={100}
                            step={10}
                            value={mapTileLayers.opacities[layer.uuid]}
                            onChange={e => mapTileLayers.setOpacity(layer.uuid, parseInt(e.currentTarget.value))}
                        />
                        <strong style={{ width: '3em' }}> {mapTileLayers.opacities[layer.uuid]}%</strong>
                    </div>
                )}
            </div>
        </div>
    );
});

const SortableLayerItem = observer(({ id, layer, mapTileLayers, mapWrapper }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    };

    const dragHandleProps = {
        ...attributes,
        ...listeners,
    };

    return (
        <div ref={setNodeRef}>
            <LayerItem mapWrapper={mapWrapper} layer={layer} mapTileLayers={mapTileLayers} dragHandleProps={dragHandleProps} style={style} />
        </div>
    );
});
